/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import { getAdressBarHeight } from "selectors/appUiStateSelectors";
import {
  getShowWaitView,
  getLiquidModeIframeSrc,
  getLiquidModeViewError,
  getLiquidModeViewReady,
  getNetworkProxyReady,
  getNetworkProxyTechnology,
  isDocumentInLiquidMode,
  setViewState,
  setNetworkProxyReady,
  setNetworkProxyTechnology,
  getServiceWorkerScope,
  getLiquidModeViewLoaded,
  shouldTriggerTranslation
} from "selectors/liquidModeViewSelectors";
import {
  getLMPersonalisationFontSize,
  getLMPersonalisationLineHeight,
  getLMPersonalisationLetterSpacing
} from "selectors/liquidModePersonalisationSelectors";
import { getPageBeforeOrAtOffset } from "selectors/pageNumber";
import LiquidModeViewController from "components/liquidMode/LiquidModeViewController";
import { trackDXBundleLoaded } from "analytics/events";
import { liquidModeViewState } from "constants/enums";
import { transformViewToPDF } from "dc-preview-utils/src/Utils/PageUtils";
import { getPageOrigHeight } from "selectors/pageSelectors";
import { LM_PERSONALISATION, FILENAME_TOAST_HIDE_TIMEOUT } from "constants/constants";
import { getErrorMsg } from "utils/LiquidModeUtils";

const mapStoreToProps = stores => {
  const liquidModeViewLoaded = getLiquidModeViewLoaded(stores);
  const { yInPage, pageIndex } = getPageBeforeOrAtOffset(stores);
  const yInPDF = liquidModeViewLoaded ? transformViewToPDF(0, yInPage, pageIndex, stores).y : 0;
  const { fontSizeUnit, letterSpacingUnit } = LM_PERSONALISATION;
  return {
    networkProxyTechnology: getNetworkProxyTechnology(stores),
    setNetworkProxyTechnology: setNetworkProxyTechnology(stores),
    serviceWorkerScope: getServiceWorkerScope(stores),
    networkProxyReady: getNetworkProxyReady(stores),
    setNetworkProxyReady: setNetworkProxyReady(stores),
    exitLiquidMode: flag => {
      stores.liquidModeViewStore.exitLiquidMode(flag);
    },
    isDocumentInLiquidMode: isDocumentInLiquidMode(stores),
    setDisqualificationSeverity: severity => {
      stores.liquidModeViewStore.setDisqualificationSeverity(severity);
    },
    showWaitView: getShowWaitView(stores),
    waitViewStyle: {
      bottom: `${getAdressBarHeight(stores) / 2}px`
    },
    showLiquidModeIframe: getNetworkProxyReady(stores),
    liquidModeIframeSrc: getLiquidModeIframeSrc(stores),
    liquidModeViewReady: getLiquidModeViewReady(stores),
    liquidModeViewReadyCallback: () => {
      const _setViewState = setViewState(stores);
      _setViewState(liquidModeViewState.READY);

      trackDXBundleLoaded();
    },
    liquidModeViewLoaded,
    liquidModeViewLoadedCallback: () => {
      const _setViewState = setViewState(stores);
      _setViewState(liquidModeViewState.LOADED);
    },
    error: getLiquidModeViewError(stores),
    liquidModeViewErrorCallback: e => {
      stores.liquidModeViewStore.setError(e);
    },
    notifyError: () => {
      const { type } = getLiquidModeViewError(stores);
      const errorMsgString = getErrorMsg(type);
      stores.notificationViewStore.addNotification(errorMsgString, {
        timeout: FILENAME_TOAST_HIDE_TIMEOUT,
        variant: "error",
        type: "banner"
      });
    },
    pageIndex,
    yInPDF,
    shouldTriggerTranslation: shouldTriggerTranslation(stores),
    scrollToPDFLocation: ({ y, pageIndex }) => {
      const xInPDF = 0;
      const yInPDF = getPageOrigHeight(stores, pageIndex) - y;
      stores.pagedViewState.goToLocation(pageIndex, xInPDF, yInPDF);
    },
    fontSize: getLMPersonalisationFontSize(stores),
    lineHeight: getLMPersonalisationLineHeight(stores),
    letterSpacing: getLMPersonalisationLetterSpacing(stores),
    fontSizeUnit,
    letterSpacingUnit
  };
};

const LiquidModeViewContainer = inject(mapStoreToProps)(LiquidModeViewController);

export { LiquidModeViewContainer };
